// card-sandbox: host-owned persistence for sandboxed cards.
//
// An opaque-origin sandboxed iframe cannot use localStorage (it throws), and we
// would not want it to anyway — that's the PARENT origin's storage, holding the
// session and the webpush VAPID material. So the host owns persistence and the
// card requests it over postMessage (see handleCardMessage CARD.STORAGE). This
// backs that host store on the parent's localStorage under a namespaced key the
// card never sees or controls.
//
// Scope is (account, kind): a card's storage is per logged-in account AND per card
// kind, so one card can't read another card's state and a shared device doesn't
// leak one user's unlocks to the next.

const PREFIX = 'cardstore';

function keyFor(accountId, kind, key) {
  // account/kind are host-supplied (trusted); `key` is card-supplied. Encode the
  // card key so a crafted key can't smuggle a delimiter and escape its namespace.
  return `${PREFIX}.${accountId}.${kind}.${encodeURIComponent(key)}`;
}

// Returns { get(key), set(key, value) } bound to (accountId, kind). Values are
// JSON round-tripped. Every access is wrapped: private mode throws on setItem and
// storage can be absent, and a store failure must degrade (get→undefined,
// set→no-op), never throw into the message handler.
export function makeCardStore(accountId, kind) {
  return {
    get(key) {
      try {
        const raw = window.localStorage.getItem(keyFor(accountId, kind, key));
        return raw == null ? undefined : JSON.parse(raw);
      } catch (e) {
        return undefined;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(keyFor(accountId, kind, key), JSON.stringify(value));
      } catch (e) { /* private mode / quota / absent: card state just doesn't persist */ }
    },
  };
}

// Exposed for tests + the "clear a card's state" affordance.
export const _keyFor = keyFor;
